import { VIDEO_FPS, VIDEO_SAFETY_SECONDS, assertVideoBudget } from './service-policy.js';

/** Both endpoints identify frames included in the exported video. */
export interface ExportRange {
  readonly inFrame: number; readonly outFrame: number;
  /** Requested output length; omitted by older clients that retain the source pace. */
  readonly durationSeconds?: number | undefined;
}
export interface ExportTimingInput {
  readonly durationSeconds: number;
  readonly framesPerSecond?: number | undefined;
  readonly exportRange?: ExportRange | undefined;
}

/** Keep the composition clock separate from the number of frames captured. */
export function resolveExportTiming(input: ExportTimingInput) {
  const fps = input.framesPerSecond ?? VIDEO_FPS;
  if (!Number.isInteger(input.durationSeconds) || input.durationSeconds < 1 || !Number.isSafeInteger(input.durationSeconds * fps)
    || fps !== VIDEO_FPS) throw new RangeError('Invalid film timeline.');
  const filmFrames = input.durationSeconds * fps;
  const range = input.exportRange ?? { inFrame: 0, outFrame: filmFrames - 1 };
  if (!Number.isInteger(range.inFrame) || !Number.isInteger(range.outFrame)
    || range.inFrame < 0 || range.outFrame < range.inFrame || range.outFrame >= filmFrames) {
    throw new RangeError('IN and OUT must identify ordered frames within the film.');
  }
  const sourceFrames = range.outFrame - range.inFrame + 1;
  if (range.durationSeconds !== undefined && (!Number.isInteger(range.durationSeconds)
    || range.durationSeconds < 5 || range.durationSeconds > VIDEO_SAFETY_SECONDS)) throw new RangeError('Invalid selected video length.');
  const totalFrames = range.durationSeconds === undefined ? sourceFrames : range.durationSeconds * fps;
  return { ...range, filmFrames, sourceFrames, totalFrames, outputFrames: totalFrames, frameOffset: 0, startFrame: range.inFrame,
    timelineDurationSeconds: input.durationSeconds, durationSeconds: totalFrames / fps,
    billedSeconds: Math.ceil(totalFrames / fps), trimmed: sourceFrames !== filmFrames };
}

/** The same output index always samples the same source moment, including worker boundaries. */
export function exportFrameProgress(timing: ReturnType<typeof resolveExportTiming>, frame: number): number {
  const index = frame + timing.frameOffset;
  if (!Number.isInteger(frame) || frame < 0 || frame >= timing.totalFrames || index >= timing.outputFrames) throw new RangeError('Invalid output frame.');
  const source = timing.outputFrames === timing.sourceFrames ? timing.inFrame + index
    : timing.inFrame + index / (timing.outputFrames - 1) * (timing.outFrame - timing.inFrame);
  return source / (timing.filmFrames - 1);
}

/** Rendering and allowance checks share the same minimum and selected duration. */
export function assertExportBudget(input: ExportTimingInput, maximum = VIDEO_SAFETY_SECONDS): void {
  assertVideoBudget(input.durationSeconds, VIDEO_SAFETY_SECONDS);
  const timing = resolveExportTiming(input);
  if (!Number.isInteger(maximum) || maximum < 5 || timing.sourceFrames < 5 * VIDEO_FPS
    || timing.durationSeconds > Math.min(maximum, VIDEO_SAFETY_SECONDS)) {
    throw new RangeError(`Export length must be between 5 and ${Math.min(maximum, VIDEO_SAFETY_SECONDS)} seconds.`);
  }
}

/** Fields exposed in job history describe the delivered clip. */
export function exportJobTiming(input: ExportTimingInput) {
  const timing = resolveExportTiming(input);
  return { durationSeconds: timing.durationSeconds,
    ...(input.exportRange ? { exportRange: { ...input.exportRange }, timelineDurationSeconds: input.durationSeconds } : {}) };
}
